import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  countButtonsByKind,
  emptyCopyCodeButton,
  emptyPhoneButton,
  emptyQuickReply,
  emptyUrlButton,
  resolveUrlButtonTargetUrl,
  TEMPLATE_BUTTON_LABEL_MAX,
  TEMPLATE_BUTTON_MAX_COPY_CODE,
  TEMPLATE_BUTTON_MAX_PHONE,
  TEMPLATE_BUTTON_MAX_TOTAL,
  TEMPLATE_BUTTON_MAX_URL,
  type TemplateButtonDraft,
  type UrlButtonMode,
} from './whatsappTemplateButtons';
import type { TemplateFormState } from './whatsappTemplateForm';
import NotificationDeepLinkPicker from './NotificationDeepLinkPicker';
import { fetchDeepLinkCampaigns } from '../../services/MasterAdmin/deepLinkService';
import { fetchWhatsAppHubSettings } from '../../services/MasterAdmin/whatsappHubService';
import type { DeepLinkCampaign } from '../../models/MasterAdmin/deepLink';
import type { WhatsAppContextField } from '../../models/MasterAdmin/whatsappHub';
import { previewDeepLinkFromCampaign } from './whatsappDeepLinkPreview';

const inputClass =
  'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900';

interface WhatsAppTemplateButtonsEditorProps {
  form: TemplateFormState;
  setForm: React.Dispatch<React.SetStateAction<TemplateFormState>>;
}

function ButtonRow({
  title,
  children,
  onRemove,
}: {
  title: string;
  children: React.ReactNode;
  onRemove: () => void;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900/50">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{title}</span>
        <button
          type="button"
          onClick={onRemove}
          className="rounded p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
          aria-label="Remove button"
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </div>
      {children}
    </div>
  );
}

export const WhatsAppTemplateButtonsEditor: React.FC<WhatsAppTemplateButtonsEditorProps> = ({
  form,
  setForm,
}) => {
  const counts = useMemo(() => countButtonsByKind(form.buttons), [form.buttons]);
  const [expanded, setExpanded] = React.useState(true);
  const [deepLinkCampaigns, setDeepLinkCampaigns] = useState<DeepLinkCampaign[]>([]);
  const [contextFields, setContextFields] = useState<WhatsAppContextField[]>([]);

  const campaignsById = useMemo(
    () => new Map(deepLinkCampaigns.map((c) => [c.id, c])),
    [deepLinkCampaigns]
  );

  const reloadDeepLinkCampaigns = useCallback(async () => {
    try {
      const campaigns = await fetchDeepLinkCampaigns();
      setDeepLinkCampaigns(campaigns || []);
    } catch {
      setDeepLinkCampaigns([]);
    }
  }, []);

  useEffect(() => {
    void reloadDeepLinkCampaigns();
    fetchWhatsAppHubSettings()
      .then((res) => {
        const catalog = res.data.referenceGuide?.variableCatalog;
        if (catalog?.allContextFields?.length) {
          setContextFields(catalog.allContextFields);
        }
      })
      .catch(() => setContextFields([]));
  }, [reloadDeepLinkCampaigns]);

  useEffect(() => {
    if (!deepLinkCampaigns.length) return;
    const map = new Map(deepLinkCampaigns.map((c) => [c.id, c]));
    setForm((prev) => {
      let changed = false;
      const buttons = prev.buttons.map((b) => {
        if (b.kind !== 'URL' || (b.urlMode || 'https') !== 'deeplink' || !b.deepLinkCampaignId) return b;
        const preview = previewDeepLinkFromCampaign(
          {
            deepLinkCampaignId: b.deepLinkCampaignId,
            deepLinkUrlType: b.deepLinkUrlType,
            deepLinkParamBindings: b.deepLinkParamBindings,
          },
          map
        );
        if (preview && preview !== b.url) {
          changed = true;
          return { ...b, url: preview };
        }
        return b;
      });
      return changed ? { ...prev, buttons } : prev;
    });
  }, [deepLinkCampaigns, setForm]);

  const upsertCampaign = (campaign: DeepLinkCampaign) => {
    setDeepLinkCampaigns((prev) => [campaign, ...prev.filter((c) => c.id !== campaign.id)]);
  };

  const setUrlButtonMode = (id: string, urlMode: UrlButtonMode) => {
    setForm((prev) => ({
      ...prev,
      buttons: prev.buttons.map((b) => {
        if (b.id !== id || b.kind !== 'URL') return b;
        if (urlMode === 'deeplink') {
          return {
            ...b,
            urlMode,
            url: '',
            deepLinkCampaignId: b.deepLinkCampaignId || null,
            deepLinkUrlType: b.deepLinkUrlType || 'short',
            deepLinkParamBindings: b.deepLinkParamBindings || {},
          };
        }
        return {
          ...b,
          urlMode,
          url: b.url?.trim() && b.url !== 'https://' ? b.url : 'https://',
        };
      }),
    }));
  };

  const updateUrlDeepLink = (id: string, value: {
    deepLinkCampaignId?: string | null;
    deepLinkUrlType?: 'short' | 'full' | 'dynamic';
    deepLinkParamBindings?: Record<string, string>;
  }) => {
    setForm((prev) => ({
      ...prev,
      buttons: prev.buttons.map((b) => {
        if (b.id !== id || b.kind !== 'URL') return b;
        const next = {
          ...b,
          urlMode: 'deeplink' as const,
          deepLinkCampaignId: value.deepLinkCampaignId ?? b.deepLinkCampaignId,
          deepLinkUrlType: value.deepLinkUrlType ?? b.deepLinkUrlType ?? 'short',
          deepLinkParamBindings: value.deepLinkParamBindings ?? b.deepLinkParamBindings ?? {},
        };
        const preview = previewDeepLinkFromCampaign(
          {
            deepLinkCampaignId: next.deepLinkCampaignId,
            deepLinkUrlType: next.deepLinkUrlType,
            deepLinkParamBindings: next.deepLinkParamBindings,
          },
          campaignsById
        );
        return { ...next, url: preview || next.url };
      }),
    }));
  };

  const updateButtons = (buttons: TemplateButtonDraft[]) => {
    setForm((prev) => ({ ...prev, buttons }));
  };

  const updateButton = (id: string, patch: Partial<TemplateButtonDraft>) => {
    setForm((prev) => ({
      ...prev,
      buttons: prev.buttons.map((b) => (b.id === id ? ({ ...b, ...patch } as TemplateButtonDraft) : b)),
    }));
  };

  const removeButton = (id: string) => {
    setForm((prev) => ({ ...prev, buttons: prev.buttons.filter((b) => b.id !== id) }));
  };

  const canAddMore = counts.total < TEMPLATE_BUTTON_MAX_TOTAL;

  const addQuickReply = () => {
    if (!canAddMore) return;
    updateButtons([...form.buttons, emptyQuickReply()]);
  };

  const addUrl = () => {
    if (!canAddMore || counts.url >= TEMPLATE_BUTTON_MAX_URL) return;
    updateButtons([...form.buttons, emptyUrlButton()]);
  };

  const addPhone = () => {
    if (!canAddMore || counts.phone >= TEMPLATE_BUTTON_MAX_PHONE) return;
    updateButtons([...form.buttons, emptyPhoneButton()]);
  };

  const addCopyCode = () => {
    if (!canAddMore || counts.copyCode >= TEMPLATE_BUTTON_MAX_COPY_CODE) return;
    updateButtons([...form.buttons, emptyCopyCodeButton()]);
  };

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
      >
        <div>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">Buttons</p>
          <p className="text-xs text-gray-500">
            {counts.total}/{TEMPLATE_BUTTON_MAX_TOTAL} buttons · Quick replies, URLs, phone, copy code
          </p>
        </div>
        {expanded ? (
          <ChevronUpIcon className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-gray-400" />
        )}
      </button>

      {expanded && (
        <div className="space-y-4 border-t border-gray-200 px-4 py-4 dark:border-gray-700">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={!canAddMore}
              onClick={addQuickReply}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:hover:bg-gray-800"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Quick reply
            </button>
            <button
              type="button"
              disabled={!canAddMore || counts.url >= TEMPLATE_BUTTON_MAX_URL}
              onClick={addUrl}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:hover:bg-gray-800"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Website URL ({counts.url}/{TEMPLATE_BUTTON_MAX_URL})
            </button>
            <button
              type="button"
              disabled={!canAddMore || counts.phone >= TEMPLATE_BUTTON_MAX_PHONE}
              onClick={addPhone}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:hover:bg-gray-800"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Phone number
            </button>
            <button
              type="button"
              disabled={!canAddMore || counts.copyCode >= TEMPLATE_BUTTON_MAX_COPY_CODE}
              onClick={addCopyCode}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium hover:bg-gray-50 disabled:opacity-40 dark:border-gray-600 dark:hover:bg-gray-800"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              Copy code
            </button>
          </div>

          {form.buttons.length === 0 ? (
            <p className="text-xs text-gray-500">No buttons yet. Add at least one CTA or quick reply.</p>
          ) : (
            <div className="space-y-3">
              {form.buttons.map((btn) => {
                if (btn.kind === 'URL') {
                  const resolvedUrl = resolveUrlButtonTargetUrl(btn, campaignsById);
                  return (
                    <ButtonRow key={btn.id} title="Website URL" onRemove={() => removeButton(btn.id)}>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                          <label className="mb-1 block text-xs text-gray-500">Button label</label>
                          <input
                            className={inputClass}
                            maxLength={TEMPLATE_BUTTON_LABEL_MAX}
                            value={btn.text}
                            onChange={(e) => updateButton(btn.id, { text: e.target.value })}
                            placeholder="Visit website"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="mb-1 block text-xs text-gray-500">Link type</label>
                          <div className="flex flex-wrap gap-2">
                            {(
                              [
                                { id: 'https' as const, label: 'HTTPS website' },
                                { id: 'deeplink' as const, label: 'Deep link' },
                              ] as const
                            ).map(({ id, label }) => (
                              <label
                                key={id}
                                className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium ${
                                  (btn.urlMode || 'https') === id
                                    ? 'border-[#25D366] bg-[#25D366]/10 text-[#128C7E]'
                                    : 'border-gray-200 dark:border-gray-600'
                                }`}
                              >
                                <input
                                  type="radio"
                                  name={`urlMode-${btn.id}`}
                                  className="sr-only"
                                  checked={(btn.urlMode || 'https') === id}
                                  onChange={() => setUrlButtonMode(btn.id, id)}
                                />
                                {label}
                              </label>
                            ))}
                          </div>
                        </div>
                        {(btn.urlMode || 'https') === 'https' ? (
                          <div className="sm:col-span-2">
                            <label className="mb-1 block text-xs text-gray-500">
                              URL (https only — domain is fixed, path can change when sending)
                            </label>
                            <input
                              className={inputClass}
                              value={btn.url}
                              onChange={(e) =>
                                updateButton(btn.id, { urlMode: 'https', url: e.target.value })
                              }
                              placeholder="https://vedika.health"
                            />
                          </div>
                        ) : (
                          <div className="sm:col-span-2">
                            <NotificationDeepLinkPicker
                              compact
                              value={{
                                deepLinkCampaignId: btn.deepLinkCampaignId,
                                deepLinkUrlType: btn.deepLinkUrlType || 'short',
                                deepLinkParamBindings: btn.deepLinkParamBindings || {},
                              }}
                              onChange={(value) => updateUrlDeepLink(btn.id, value)}
                              contextFields={contextFields}
                              campaigns={deepLinkCampaigns}
                              onCampaignCreated={upsertCampaign}
                              onCampaignUpdated={upsertCampaign}
                              onRefreshCampaigns={() => void reloadDeepLinkCampaigns()}
                            />
                            {resolvedUrl ? (
                              <p className="mt-2 break-all text-[11px] text-gray-500">
                                Sample URL for approval:{' '}
                                <span className="font-mono text-gray-700 dark:text-gray-300">{resolvedUrl}</span>
                              </p>
                            ) : (
                              <p className="mt-2 text-[11px] text-amber-700 dark:text-amber-300">
                                Select a deep link campaign with a short https URL for Meta approval.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </ButtonRow>
                  );
                }
                if (btn.kind === 'PHONE_NUMBER') {
                  return (
                    <ButtonRow key={btn.id} title="Phone number" onRemove={() => removeButton(btn.id)}>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-xs text-gray-500">Button label</label>
                          <input
                            className={inputClass}
                            maxLength={TEMPLATE_BUTTON_LABEL_MAX}
                            value={btn.text}
                            onChange={(e) => updateButton(btn.id, { text: e.target.value })}
                            placeholder="Call us"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs text-gray-500">Phone (E.164)</label>
                          <input
                            className={inputClass}
                            value={btn.phoneNumber}
                            onChange={(e) => updateButton(btn.id, { phoneNumber: e.target.value })}
                            placeholder="+919876543210"
                          />
                        </div>
                      </div>
                    </ButtonRow>
                  );
                }
                if (btn.kind === 'COPY_CODE') {
                  return (
                    <ButtonRow key={btn.id} title="Copy offer code" onRemove={() => removeButton(btn.id)}>
                      <div className="grid gap-2 sm:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-xs text-gray-500">Button label</label>
                          <input
                            className={inputClass}
                            maxLength={TEMPLATE_BUTTON_LABEL_MAX}
                            value={btn.text}
                            onChange={(e) => updateButton(btn.id, { text: e.target.value })}
                            placeholder="Copy offer code"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs text-gray-500">Sample code (Meta review)</label>
                          <input
                            className={inputClass}
                            value={btn.code}
                            onChange={(e) => updateButton(btn.id, { code: e.target.value })}
                            placeholder="WELCOME25"
                          />
                        </div>
                      </div>
                    </ButtonRow>
                  );
                }
                return (
                  <ButtonRow key={btn.id} title="Quick reply" onRemove={() => removeButton(btn.id)}>
                    <div>
                      <label className="mb-1 block text-xs text-gray-500">Button label</label>
                      <input
                        className={inputClass}
                        maxLength={TEMPLATE_BUTTON_LABEL_MAX}
                        value={btn.text}
                        onChange={(e) => updateButton(btn.id, { text: e.target.value })}
                        placeholder="Yes, interested"
                      />
                    </div>
                  </ButtonRow>
                );
              })}
            </div>
          )}

          {form.category === 'MARKETING' && (
            <label className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={form.includeOptOut}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    includeOptOut: e.target.checked,
                    footer: e.target.checked ? prev.footer || 'Reply STOP to opt out' : prev.footer,
                  }))
                }
              />
              <span>
                <strong>Add STOP opt-out</strong> — Meta requires marketing templates to let users unsubscribe.
                Adds footer text and a Stop quick-reply. If you also add a website/deep-link button, Interakt
                only accepts that Call To Action via API; Stop is still saved locally and the footer is sent.
              </span>
            </label>
          )}
        </div>
      )}
    </div>
  );
};

export default WhatsAppTemplateButtonsEditor;
